import { cn } from '@/utilities/ui'
import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

/**
 * Wilson Property canonical Input component.
 *
 * Figma spec (component set 2198:38512 — verified 2026-07-22):
 *   Model     FILLED grey field, not an outlined white one. Fill `theme/muted` #e0dfe0,
 *             radius `sm` 2px, NO border on Empty / Placeholder / Value / Focused / Disabled.
 *   Sizes     Default h36 (2198:38513) · Small h32 (2198:38515) — both px-3 (`padding/p-3`)
 *             py-1 (`padding/p-1`) with the same type.
 *   Text      Body/bodySm — Lato light 300, `font/size/sm` 14 / `font/lineHeight/sm` 16
 *             (the `text-body-sm` token), colour `theme/foreground`;
 *             placeholder `theme/muted-foreground` #5a595d.
 *   Shadow    `shadows/shadow-xs` 0 1px 2px rgba(0,0,0,0.05).
 *   Focus     `focus` effect only — 0 0 0 3px `theme/ring` #8cc64080. No border is added
 *             or recoloured on focus (2198:38527).
 *   Error     1px solid `theme/destructive` #ef4444 AND the value text turns destructive
 *             (2198:38531 / 2198:38532). Error alone has NO ring; Error Focused
 *             (2198:38535) adds `focus-error` 0 0 0 3px `color/status/error-300` #fca5a5
 *             at full opacity.
 *   Disabled  opacity 50% (2198:38517).
 *   File      Input File 2198:38563 — "Choose File" is Lato regular 400, 14/16,
 *             colour `theme/foreground`.
 *
 * #fca5a5 (`color/status/error-300`) has no token in globals.css yet — see the parity
 * escalation list (button.tsx carries the same literal for the same reason).
 */
const inputVariants = cva(
  // NOTE: the type size is written as `text-[length:var(--text-body-sm)]` rather than the
  // `text-body-sm` shorthand because tailwind-merge cannot tell a custom `text-body-*`
  // font-size from a `text-*` colour, and silently drops it in favour of `text-foreground`.
  // The `length:` label disambiguates it. Both forms resolve to the same token.
  'flex w-full min-w-0 rounded-sm bg-muted px-3 py-1 text-[length:var(--text-body-sm)] leading-[var(--text-body-sm--line-height)] font-light text-foreground shadow-xs placeholder:text-muted-foreground placeholder:font-light transition-[color,box-shadow] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring focus-visible:ring-offset-0 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border aria-invalid:border-destructive aria-invalid:text-destructive aria-invalid:focus-visible:ring-[#fca5a5]! file:border-0 file:bg-transparent file:text-[length:var(--text-body-sm)] file:font-normal file:text-foreground',
  {
    variants: {
      size: {
        // default = Figma Size/Default — 36px tall
        default: 'h-9',
        // sm = Figma Size/Small — 32px tall, same padding and type
        sm: 'h-8',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
)

export interface InputProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {}

const Input: React.FC<InputProps> = ({ className, size, type, ...props }) => {
  return (
    <input
      data-slot="input"
      className={cn(inputVariants({ size, className }))}
      type={type}
      {...props}
    />
  )
}

export { Input, inputVariants }
